import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowUpRight, ArrowLeftRight, Wallet, Plus, Activity, Clock } from "lucide-react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { api, fmtUSD, fmtUSD2 } from "@/lib/apiClient";
import { useDicts } from "@/hooks/useDicts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import PeriodPicker, { presetRange } from "@/components/PeriodPicker";
import OperationDialog from "@/components/OperationDialog";
import OperationsTable from "@/components/OperationsTable";

const PIE_COLORS = ["#3B82F6", "#10B981", "#EF4444", "#8B5CF6", "#F59E0B", "#06B6D4", "#EC4899", "#84CC16"];

const Metric = ({ label, value, sub, icon: Icon, tone = "primary", testid }) => (
  <Card className="metric-card glass" data-testid={testid}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon className={`h-4 w-4 text-${tone}`} />
      </div>
      <div className="mt-3 font-mono text-2xl font-bold tracking-tight lg:text-3xl">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const [range, setRange] = useState({ ...presetRange("month"), preset: "month" });
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(false);
  const { dicts, reload } = useDicts();

  const load = useCallback(async () => {
    const { data } = await api.get("/dashboard", { params: { date_from: range.from, date_to: range.to } });
    setData(data);
  }, [range.from, range.to]);

  useEffect(() => {
    load();
  }, [load]);

  const t = data?.totals || {};

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold sm:text-4xl">Дэшборд</h1>
          <p className="mt-1 text-sm text-muted-foreground">Финансовая сводка по всем компаниям в USD</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <PeriodPicker range={range} onChange={setRange} />
          <Button className="gap-2" data-testid="dashboard-new-operation" onClick={() => setDialog(true)}>
            <Plus className="h-4 w-4" /> Операция
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 md:gap-6">
        <Metric label="Общий остаток" value={fmtUSD(data?.total_balance)} icon={Wallet} testid="metric-total-balance"
          sub={`${data?.companies?.length || 0} компаний`} />
        <Metric label="Доход за период" value={fmtUSD(t.income)} icon={ArrowUpRight} tone="income"
          sub={`Ожидается ещё ${fmtUSD(t.income_planned)}`} testid="metric-income" />
        <Metric label="Расход за период" value={fmtUSD(t.expense)} icon={ArrowDownRight} tone="expense"
          sub={`Запланировано ${fmtUSD(t.expense_planned)}`} testid="metric-expense" />
        <Metric label="Операций" value={t.count || 0} icon={Activity}
          sub={`Переводы: ${fmtUSD(t.transfer_out)}`} testid="metric-operations" />
      </div>

      <div className="grid gap-4 md:grid-cols-3 md:gap-6">
        <Metric label="Чистый результат" value={fmtUSD2(t.net)} icon={ArrowLeftRight} testid="metric-net"
          sub="Доход + входящие переводы − расходы" />
        <Metric label="Ожидаемые поступления" value={fmtUSD(t.income_planned)} icon={Clock} tone="warning"
          sub="Неподтверждённые доходы" testid="metric-income-planned" />
        <Metric label="Всего запланировано" value={data?.planned_count || 0} icon={Clock} tone="warning"
          sub="Операции, ожидающие подтверждения" testid="metric-planned-count" />
      </div>

      <Card className="glass">
        <CardContent className="p-5 sm:p-6">
          <h2 className="mb-4 text-xl font-semibold">Остатки по компаниям</h2>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {(data?.companies || []).map((c) => (
              <Link key={c.id} to={`/pnl/${c.id}`} data-testid={`company-balance-${c.id}`}>
                <div className="metric-card rounded-xl border bg-card/60 p-4">
                  <div className="truncate text-sm font-semibold">{c.name}</div>
                  <div className="mt-2 font-mono text-xl font-bold">{fmtUSD(c.balance)}</div>
                  <div className="mt-2 space-y-0.5 text-xs">
                    <div className="text-income">+{fmtUSD(c.income)} доход</div>
                    <div className="text-expense">−{fmtUSD(c.expense)} расход</div>
                    <div className="text-muted-foreground">{c.count} операций</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3 md:gap-6">
        <Card className="glass lg:col-span-2">
          <CardContent className="p-5 sm:p-6">
            <h2 className="mb-4 text-xl font-semibold">Динамика по месяцам</h2>
            <div className="h-[280px]" data-testid="chart-series">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data?.series || []}>
                  <defs>
                    <linearGradient id="gi" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="ge" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#EF4444" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#EF4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
                  <XAxis dataKey="month" stroke="#9CA3AF" fontSize={11} />
                  <YAxis stroke="#9CA3AF" fontSize={11} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <Tooltip
                    contentStyle={{ background: "#111827", border: "1px solid #1F2937", borderRadius: 8, fontSize: 12 }}
                    formatter={(v, n) => [fmtUSD2(v), n === "income" ? "Доход" : n === "expense" ? "Расход" : "Переводы"]}
                  />
                  <Legend formatter={(v) => (v === "income" ? "Доход" : v === "expense" ? "Расход" : "Переводы")} />
                  <Area type="monotone" dataKey="income" stroke="#10B981" fill="url(#gi)" strokeWidth={2} />
                  <Area type="monotone" dataKey="expense" stroke="#EF4444" fill="url(#ge)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="glass">
          <CardContent className="p-5 sm:p-6">
            <h2 className="mb-4 text-xl font-semibold">Расходы по категориям</h2>
            <div className="h-[280px]" data-testid="chart-categories">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data?.top_categories || []} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={2}>
                    {(data?.top_categories || []).map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#111827", border: "1px solid #1F2937", borderRadius: 8, fontSize: 12 }}
                    formatter={(v) => fmtUSD2(v)}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="glass">
        <CardContent className="p-5 sm:p-6">
          <h2 className="mb-4 text-xl font-semibold">Доход и расход по компаниям</h2>
          <div className="h-[260px]" data-testid="chart-companies">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.companies || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
                <XAxis dataKey="name" stroke="#9CA3AF" fontSize={10} />
                <YAxis stroke="#9CA3AF" fontSize={11} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip
                  contentStyle={{ background: "#111827", border: "1px solid #1F2937", borderRadius: 8, fontSize: 12 }}
                  formatter={(v, n) => [fmtUSD2(v), n === "income" ? "Доход" : "Расход"]}
                />
                <Bar dataKey="income" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expense" fill="#EF4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div>
        <h2 className="mb-4 text-xl font-semibold">Последние операции</h2>
        <OperationsTable operations={data?.recent || []} />
      </div>

      <OperationDialog
        open={dialog}
        onOpenChange={setDialog}
        dicts={dicts}
        reloadDicts={reload}
        onSaved={load}
      />
    </div>
  );
}
